import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, Button } from '@ohif/ui-next';
import { generateReportHtml } from '../../../../../../extensions/radiology-report/src/utils/reportHtmlUtils';
import type {
  PatientInfo,
  FacilityInfo,
  RadiologistData,
  ReportContentData,
} from '../../../../../../extensions/radiology-report/src/utils/reportHtmlUtils';

interface ReportPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  facilityData: FacilityInfo | null;
  patientInfo?: Partial<PatientInfo>;
  radiologistData?: Partial<RadiologistData>;
  reportContent?: Partial<ReportContentData>;
  logoFile?: File | null; // Recently uploaded logo file (not yet saved to DB)
}

const ReportPreviewModal: React.FC<ReportPreviewModalProps> = ({
  isOpen,
  onClose,
  facilityData,
  patientInfo = {},
  radiologistData = {},
  reportContent = {},
  logoFile = null,
}) => {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasGeneratedRef = useRef(false);

  // Memoize default values to prevent recreation on every render
  const defaultPatientInfo: PatientInfo = useMemo(() => {
    const {
      patientName = 'John Doe',
      patientId = 'PAT001',
      patientAge = '045Y',
      patientGender = 'M',
      studyDate = new Date().toLocaleDateString('en-GB'),
      referringPhysician = 'Dr. Smith',
      studyDescription = 'X RAY - CHEST PA VIEW',
      modality = 'XRAY',
      bodyPart = 'chest',
      viewPosition = 'PA',
    } = patientInfo;

    return {
      patientName,
      patientId,
      patientAge,
      patientGender,
      studyDate,
      referringPhysician,
      studyDescription,
      modality,
      bodyPart,
      viewPosition,
    };
  }, [patientInfo]);

  const defaultRadiologistData: RadiologistData = useMemo(() => {
    const {
      name = 'Dr. Radiologist',
      qualification = 'MBBS, DMRD (Radiodiagnosis)',
      registrationNumber = 'REG123456',
      signature = null,
    } = radiologistData;

    return {
      name,
      qualification,
      registrationNumber,
      signature,
    };
  }, [radiologistData]);

  const defaultReportContent: ReportContentData = useMemo(() => {
    const {
      reportTitle = 'Radiology Report',
      contentToPrint = '',
      findingsToPrint = 'The chest X-ray shows clear lung fields bilaterally. The heart size is within normal limits. No pleural effusion or pneumothorax is noted. The bony structures appear intact.',
      impressionToPrint = 'Normal chest X-ray. No acute cardiopulmonary abnormality detected.',
      reportContent: reportContentValue = '',
      isAIMode = false,
      isDocumentTemplate = false,
    } = reportContent;

    return {
      reportTitle,
      contentToPrint,
      findingsToPrint,
      impressionToPrint,
      reportContent: reportContentValue,
      isAIMode,
      isDocumentTemplate,
    };
  }, [reportContent]);

  // Helper function to convert File to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Generate preview function - only called when modal opens
  const generatePreview = useCallback(async () => {
    if (hasGeneratedRef.current) {
      return; // Prevent multiple calls
    }

    setIsLoading(true);
    setError(null);
    hasGeneratedRef.current = true;

    try {
      // Handle logo: if logoFile is provided (recently uploaded), convert to base64
      // Otherwise, use facilityData.letterhead_logo (from DB)
      let logoBase64: string | null = null;
      if (logoFile) {
        try {
          logoBase64 = await fileToBase64(logoFile);
        } catch (err: unknown) {
          const errorMessage = err instanceof Error ? err.message : 'Unknown error';
          console.error('Error converting logo file to base64:', errorMessage);
        }
      }

      // Create facility data with logo (either from File or DB)
      const facilityDataWithLogo = facilityData
        ? {
            ...facilityData,
            letterhead_logo: logoBase64 ?? facilityData.letterhead_logo ?? null,
          }
        : null;

      const html = await generateReportHtml({
        reportContent: defaultReportContent,
        patientInfo: defaultPatientInfo,
        facilityData: facilityDataWithLogo,
        radiologistData: defaultRadiologistData,
        isPreview: true, // Show placeholder text instead of signature image
      });

      setHtmlContent(html);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate report preview';
      console.error('Error generating report preview:', errorMessage);
      setError(errorMessage);
      hasGeneratedRef.current = false; // Allow retry on error
    } finally {
      setIsLoading(false);
    }
  }, [defaultReportContent, defaultPatientInfo, facilityData, defaultRadiologistData, logoFile]);

  // Only generate preview when modal opens (not on every render)
  useEffect(() => {
    if (isOpen) {
      // Reset the ref when modal opens to allow regeneration
      hasGeneratedRef.current = false;
      generatePreview();
    } else {
      // Reset state when modal closes
      setHtmlContent('');
      setError(null);
      hasGeneratedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen to prevent infinite loops

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(isDialogOpen: boolean) => !isDialogOpen && onClose()}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
    >
      <DialogContent className="flex h-[90vh] min-w-[70vw] max-w-[900px] flex-col">
        <DialogHeader>
          <DialogTitle>Report Preview</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 flex-col overflow-hidden">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <div className="mb-2 text-lg">Generating preview...</div>
                <div className="text-sm text-gray-400">Please wait</div>
              </div>
            </div>
          ) : error ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center text-red-400">
                <div className="mb-2 text-lg">Error</div>
                <div className="text-sm">{error}</div>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-4 flex justify-end gap-2">
                <Button
                  variant="secondary"
                  onClick={onClose}
                >
                  Close
                </Button>
              </div>
              <div className="flex-1 overflow-auto rounded border border-gray-700 bg-white">
                <iframe
                  srcDoc={htmlContent}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  title="Report Preview"
                />
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReportPreviewModal;
